import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import styled from 'styled-components';
import cx from 'classnames';

import { ProgressBar, LoadingSpinner, Button } from '../../components/_shared';
import AccountLayout from '../../components/layout/Account';
import { makeSelectUserId, makeSelectUser } from '../../store/selectors';
import { getUserProgress } from '../../service/user';
import arrowDownIcon from '../../assets/imgs/arrow-down.png';
import './Progress.scss';
import { notify } from '../../utils/helper';

const Curriculum = styled.div.attrs({
  className: 'bg-white rounded-xl flex flex-col',
})`
  ${(props) =>
    props.isCore &&
    `
  `}
`;

const Member = ({ memberId }) => {
  const history = useHistory();
  const { name } = useSelector(makeSelectUser());
  const [loading, setLoading] = useState(false);
  const [program, setProgram] = useState({});
  const [curriculums, setCurriculums] = useState([]);
  const [currentProgram, setCurrentProgram] = useState(null);
  const [selectedCurriculum, setSelectedCurriculum] = useState(null);
  const [progress, setProgress] = useState([]);

  useEffect(() => {
    const fetchUserProgress = async () => {
      setLoading(true);

      try {
        const res = await getUserProgress(memberId);
        const { user, program: userProgram } = res.data.data;

        setProgress(user.progress);

        if (!userProgram) {
          return;
        }

        userProgram.curriculums.forEach((curriculum) => {
          curriculum.courses.forEach((course) => {
            const userCourse = user.progress.find(
              ({ course: coourse }) => coourse && coourse._id === course._id
            );

            if (userCourse) {
              course.total = course.lessons.length + course.quizzes.length;
              course.current =
                userCourse.completedLessons.length +
                userCourse.completedQuizzes.length;
              course.percent =
                ((userCourse.completedLessons.length +
                  userCourse.completedQuizzes.length) /
                  parseFloat(course.lessons.length + course.quizzes.length)) *
                100;
            } else {
              course.percent = 0;
              course.total = course.lessons.length + course.quizzes.length;
              course.current = 0;
            }
          });
        });

        setProgram(userProgram);
        setCurriculums(userProgram.curriculums);
        setLoading(false);
      } catch (err) {
        console.log('[fetching.user.progress]', err);
        notify(
          'error',
          'Error in fetching user progress: ' + (err && err.toString())
        );
      }
    };

    fetchUserProgress();
  }, [memberId]);

  const handleSelectProgram = (program) => () => setCurrentProgram(program);
  const handleToggleCurriculum = (id) => () => {
    if (selectedCurriculum === id) {
      setSelectedCurriculum(null);
      return;
    }

    setSelectedCurriculum(id);
  };

  // if (loading) {
  //   return (
  //     <AccountLayout>
  //       <div className="w-full h-48 m-auto max-w-6xl flex items-center justify-center">
  //         <LoadingSpinner />
  //       </div>
  //     </AccountLayout>
  //   );
  // }

  const coursePercent = useCallback(
    (course) => {
      const userCourse = progress.find(
        ({ course: coourse }) => coourse && coourse._id === course._id
      );

      if (userCourse) {
        const total =
          userCourse.course.lessons.length + userCourse.course.quizzes.length;
        const current =
          userCourse.completedLessons.length +
          userCourse.completedQuizzes.length;
        return Math.min(100, (current / total) * 100);
      } else {
        return 0;
      }
    },
    [progress]
  );

  const renderCurriculum = ({ isCore, id, title, courses, courseNos }) => {
    const totalPercents = courses.reduce((p, c) => {
      const userCourse = progress.find(
        ({ course: coourse }) => coourse && coourse._id === c._id
      );

      if (userCourse) {
        const total =
          userCourse.course.lessons.length + userCourse.course.quizzes.length;
        const current =
          userCourse.completedLessons.length +
          userCourse.completedQuizzes.length;
        return p + Math.min(100, (current / total) * 100);
      } else {
        return p;
      }
    }, 0);

    const totalPercent = Math.floor(parseFloat(totalPercents / courses.length));

    const coursesWithNo = courses.map((course, id) => ({ ...course, no: courseNos[id] }));

    let sortedCourses = [...coursesWithNo];
    sortedCourses.sort((a, b) => a.no - b.no);
    sortedCourses = [
      ...sortedCourses.filter(({ no }) => !!no),
      ...sortedCourses.filter(({ no }) => !no),
    ];

    return (
      <div className="flex flex-col mb-2" key={id}>
        <Curriculum isCore={isCore} onClick={handleToggleCurriculum(id)}>
          <div className={cx('text-regular text-primary mb-2 font-bold')}>
            {title}
            <span className="text-primary ml-2 font-bold">{totalPercent}%</span>
          </div>
          <div className="w-full">
            <ProgressBar value={totalPercent} height={4} />
          </div>
          <div className="flex justify-end w-full">
            <Button
              type="flat"
              className={`mt-4 py-1 border border-solid text-primary text-base bg-transparent border-white`}
              caption={
                selectedCurriculum === id ? 'Hide Courses' : 'Show Courses'
              }
            />
          </div>
        </Curriculum>
        {selectedCurriculum === id && (
          <div className="bg-white rounded-xl mt-3">
            {sortedCourses.map((course) => (
              <div
                key={course._id}
                className="course-row flex flex-col justify-between mb-4"
              >
                <div className="course-title mb-2 font-bold">
                  {course.title}
                </div>
                <div className="flex items-center">
                  <div className="flex-grow mr-4">
                    <ProgressBar
                      value={Math.ceil(coursePercent(course))}
                      height={4}
                    />
                  </div>
                  <div className="course-status text-center text-gray-100">
                    {Math.ceil(coursePercent(course))}%
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="px-6">
      <div className="flex flex-col">
        {curriculums.map((curriculum, id) => renderCurriculum(curriculum))}
      </div>
    </div>
  );
};

export default Member;
